import * as React from 'react'
import { useEffect, useMemo } from 'react'

import { InfoIcon } from 'lucide-react'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import {
  IPermitTypeField,
  ITypeFieldFilterOptions,
  LocationType,
  PermitFieldType,
} from '~/client/graph/types.generated'
import IFormFilterFieldOption from '~/client/src/desktop/interfaces/IFormFilterFieldOption'
import TooltipWrapper from '~/client/src/shared/components/TooltipWrapper/TooltipWrapper'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'

import FieldSectionHeader from '../../../FieldSectionHeader/FieldSectionHeader'
import NewRuleButton from '../../../RuleComponents/NewRuleButton'
import DynamicFiltersList from './components/DynamicFiltersList/DynamicFiltersList'
import DynamicFiltersStore from './DynamicFilters.store'

interface IProps {
  fieldId: string
  filters: ITypeFieldFilterOptions[]
  restrictedLocationIds: string[]
  restrictedLocationTypes: LocationType[]
  filterFieldsOptions: IFormFilterFieldOption[]
  filterFieldsMap: Map<string, IPermitTypeField>
  renderHeader?: (
    enableCreationMode: () => void,
    context: {
      filtersCount: number
      isExpanded: boolean
      onToggle: () => void
    },
  ) => JSX.Element
  materialResourceRecords?: IResourceRecord[]
  isConfiguredFieldMaterialResource?: boolean
  configuredFieldType?: PermitFieldType
  disabledResourceIds?: Set<string>
  renderNewRuleButton?: (onClick: () => void) => JSX.Element

  onChange: (filters: ITypeFieldFilterOptions[]) => void
}

export default observer(DynamicFilters)

function DynamicFilters({
  fieldId,
  filters,
  filterFieldsMap,
  restrictedLocationIds,
  restrictedLocationTypes,
  filterFieldsOptions,
  materialResourceRecords,
  isConfiguredFieldMaterialResource,
  configuredFieldType,
  disabledResourceIds,
  renderHeader,
  renderNewRuleButton,
  onChange,
}: IProps) {
  const store = useMemo(
    () => new DynamicFiltersStore(filters, filterFieldsMap, onChange),
    [],
  )

  useEffect(() => {
    store.setFilters(filters)
  }, [store, filters])

  useEffect(() => {
    store.setFilterFieldsMap(filterFieldsMap)
  }, [store, filterFieldsMap])

  useEffect(() => {
    store.setOnChangeHandler(onChange)
  }, [store, onChange])

  const {
    isListExpanded,
    isCreationMode,
    filters: storeFilters,
    toggleFiltersList,
    enableCreationMode,
  } = store

  const dynamicFiltersTooltip = useMemo(
    () => (
      <div>
        <div className="mb8">
          <strong>{Localization.translator.dynamicFiltersTooltip.title}</strong>
        </div>
        <div>{Localization.translator.dynamicFiltersTooltip.description}</div>
      </div>
    ),
    [Localization.state.currentLanguage],
  )

  if (renderHeader) {
    return (
      <div className="tw:flex tw:flex-col">
        {renderHeader(enableCreationMode, {
          filtersCount: storeFilters.length,
          isExpanded: isListExpanded,
          onToggle: toggleFiltersList,
        })}
        {isListExpanded && (
          <DynamicFiltersList
            fieldId={fieldId}
            filtersStore={store}
            restrictedLocationIds={restrictedLocationIds}
            restrictedLocationTypes={restrictedLocationTypes}
            filterFieldsOptions={filterFieldsOptions}
            materialResourceRecords={materialResourceRecords}
            isConfiguredFieldMaterialResource={
              isConfiguredFieldMaterialResource
            }
            configuredFieldType={configuredFieldType}
            disabledResourceIds={disabledResourceIds}
            renderNewRuleButton={renderNewRuleButton}
          />
        )}
      </div>
    )
  }

  return (
    <div className="col pt12 mx20">
      <div
        className={classList({
          'row y-start pt5': true,
          pb10: isListExpanded && (!!storeFilters.length || isCreationMode),
        })}
      >
        <FieldSectionHeader
          text={Localization.translator.conditionalFilters}
          isExpanded={isListExpanded}
          isIconHidden={!storeFilters.length}
          onToggle={toggleFiltersList}
        />
        <TooltipWrapper
          className="row no-grow ml8"
          content={dynamicFiltersTooltip}
        >
          <InfoIcon size={16} className="tw:text-muted-foreground" />
        </TooltipWrapper>
        {!isCreationMode && <NewRuleButton onClick={enableCreationMode} />}
      </div>
      {isListExpanded && (
        <DynamicFiltersList
          fieldId={fieldId}
          filtersStore={store}
          restrictedLocationIds={restrictedLocationIds}
          restrictedLocationTypes={restrictedLocationTypes}
          filterFieldsOptions={filterFieldsOptions}
          materialResourceRecords={materialResourceRecords}
          isConfiguredFieldMaterialResource={isConfiguredFieldMaterialResource}
          disabledResourceIds={disabledResourceIds}
        />
      )}
    </div>
  )
}
